import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { CounselingJournal } from '@/domain/schema/v1';
import { JOURNAL_FORM_FIELDS } from '@/domain/schema/v1';
import { JOURNAL_FIELD_LABELS } from '@/domain/workflow/journal';
import { cleanForOutput, renderWithIdentity } from '@/domain/workflow/documents';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { ConfirmButton, ConflictBanner, SaveIndicator, SourceBadge, TextArea } from '../../components/fields';
import { AiBridge } from '@/services/aiBridge';
import { POLISH_PROMPT } from '@/domain/ai/reportWriting';
import { useRequiredSession } from '../../state/session';
import { CopyButton } from '../../components/CopyButton';
import { ReceiveBox, SendButton } from '../../components/ExternalRoundTrip';
import { DocReview, type DocPreview } from '../client/DocumentsScreen';

const STATUS: Record<CounselingJournal['status'], string> = { draft: '초안', reviewed: '검토 완료', approved: '승인' };

/** 상담기록지 (MASTER §13, UX_FLOW §13): 자동 초안 → 수정 → 승인(잠금) → 필요 시 개정본 */
export function JournalScreen() {
  const { sessionId = '' } = useParams();
  const svc = useService();
  const { data, reload } = useLoad(async () => {
    const session = await svc.getSession(sessionId);
    const [client, journal, all] = await Promise.all([
      svc.getClient(session.clientId),
      svc.createJournalDraft(sessionId),
      svc.listJournals({ sessionId }),
    ]);
    return { session, client, journal, all };
  }, [svc, sessionId]);
  if (!data) return <p className="muted">불러오는 중…</p>;
  return (
    <section className="stack">
      <div className="page-head">
        <div>
          <p className="eyebrow">
            <Link to={`/sessions/${sessionId}/run`}>‹ 회기 진행</Link>
          </p>
          <h1 className="page-title">상담기록지</h1>
          <p className="muted">
            {data.client.clientCode} · {data.session.sessionNumber}회기 · {data.session.sessionDate}
          </p>
        </div>
        <Link to={`/sessions/${sessionId}`}>회기로 돌아가기</Link>
      </div>
      <JournalEditor
        key={`${data.journal.id}:${data.journal.revision}`}
        projectUrl={data.client.externalProjectUrl}
        journal={data.journal}
        onChanged={reload}
        fileName={`${data.client.clientCode}_상담기록지_${data.session.sessionNumber}회기_${data.session.sessionDate}.docx`}
        history={
          data.all.length > 1 ? (
            <details className="card">
              <summary>이전 버전 {data.all.length - 1}개</summary>
              <ul className="plain-list small">
                {data.all
                  .filter((j) => j.id !== data.journal.id)
                  .map((j) => (
                    <li key={j.id}>
                      {STATUS[j.status]} ·{' '}
                      {j.approvedAt ? new Date(j.approvedAt).toLocaleString('ko-KR') : j.createdAt.slice(0, 10)}
                      {j.supersededById ? ' · 개정됨' : ''}
                    </li>
                  ))}
              </ul>
            </details>
          ) : null
        }
      />
    </section>
  );
}

function JournalEditor({
  journal,
  onChanged,
  fileName,
  history,
  projectUrl,
}: {
  journal: CounselingJournal;
  onChanged: () => void;
  fileName: string;
  history: React.ReactNode;
  projectUrl?: string | undefined;
}) {
  const svc = useService();
  const navigate = useNavigate();
  const locked = journal.status === 'approved';
  const [edited, setEdited] = useState<Set<string>>(new Set());
  const a = useAutosave<CounselingJournal>(
    journal,
    (patch, rev) =>
      svc.updateJournal(
        journal.id,
        patch,
        Object.keys(patch).filter((k) => edited.has(k)),
        rev,
      ),
    { readOnly: locked },
  );
  const v = a.value!;
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [withName, setWithName] = useState(false);
  /** Word(.docx) 또는 한글(.hwpx) */
  const saveFileAs = (kind: 'docx' | 'hwpx') => {
    setExporting(true);
    setError(null);
    void a
      .flush()
      .then(() =>
        Promise.all([
          svc.sessionRecordText(journal.sessionId),
          import('../../export/docx'),
          import('../../export/hwpx'),
        ]),
      )
      .then(async ([{ content, realName }, { buildDocx, saveFile }, { buildHwpx }]) => {
        const text = cleanForOutput(renderWithIdentity(content, realName, withName));
        const blob =
          kind === 'docx' ? await buildDocx(text, 'session_record') : await buildHwpx(text, 'session_record');
        await saveFile(blob, fileName.replace(/.docx$/, `.${kind}`));
      })
      .catch((e: Error) => setError(`${kind === 'docx' ? 'Word' : '한글'} 파일을 만들지 못했습니다: ${e.message}`))
      .finally(() => setExporting(false));
  };

  const setField = (k: (typeof JOURNAL_FORM_FIELDS)[number], value: string) => {
    setEdited((s) => new Set(s).add(k));
    a.set(k, value || undefined);
  };

  return (
    <div className="doc-layout">
      <div className="stack">
        {a.status === 'conflict' && (
          <ConflictBanner
            onMine={() => void svc.getJournal(journal.id).then(a.resolveWithMine)}
            onTheirs={() => void svc.getJournal(journal.id).then(a.resolveWithTheirs)}
          />
        )}
        {locked && (
          <p className="muted small">승인된 기록은 바꿀 수 없어요. 고쳐야 하면 개정본을 만드세요 (원본은 보존돼요).</p>
        )}
        {!locked && <p className="muted small">회기 분석·위험·목표에서 채웠어요. 고친 칸은 다시 덮어쓰지 않아요.</p>}
        {JOURNAL_FORM_FIELDS.map((k) => (
          <div key={k} className="card journal-field">
            <TextArea
              label={JOURNAL_FIELD_LABELS[k]}
              value={v[k]}
              readOnly={locked}
              rows={k === 'counselingContent' ? 8 : 3}
              badge={
                <SourceBadge
                  source={edited.has(k) ? 'user' : (v.fieldSources?.[k] ?? (v[k] ? undefined : 'missing'))}
                />
              }
              onChange={(x) => setField(k, x)}
            />
          </div>
        ))}
      </div>
      <aside className="doc-tools stack">
        <div className="card">
          <div className="row" style={{ alignItems: 'center', width: '100%' }}>
            <span className={locked ? 'status-pill ok' : 'status-pill'}>{STATUS[journal.status]}</span>
            <SaveIndicator status={a.status} error={a.error} onRetry={() => void a.flush()} />
          </div>
          <div className="row">
            <button type="button" className="soft-btn" onClick={() => saveFileAs('hwpx')} disabled={exporting}>
              {exporting ? '만드는 중…' : '한글 저장'}
            </button>
            <button type="button" className="soft-btn" onClick={() => saveFileAs('docx')} disabled={exporting}>
              Word 저장
            </button>
          </div>
          <label className="check small">
            <input type="checkbox" checked={withName} onChange={(e) => setWithName(e.target.checked)} /> 실명 넣기
          </label>
        </div>
        {error && (
          <p role="alert" className="warn">
            {error}
          </p>
        )}
        {!locked && (
          <ConfirmButton
            className="primary run-next"
            label="상담기록지 승인"
            confirmText="승인하면 이 기록은 잠기고, 다음 회기 준비 자료가 자동으로 만들어집니다."
            onConfirm={() =>
              void a
                .flush()
                .then(() => svc.approveJournal(journal.id))
                .then(() => navigate(`/sessions/${journal.sessionId}`))
                .catch((e: Error) => setError(e.message))
            }
          />
        )}
        {!locked && journal.status === 'draft' && (
          <button
            type="button"
            className="link small"
            onClick={() =>
              void a
                .flush()
                .then(() => svc.markJournalReviewed(journal.id))
                .then(onChanged)
            }
          >
            검토 완료로 표시 (승인은 나중에)
          </button>
        )}
        {locked && (
          <ConfirmButton
            label="개정본 만들기"
            confirmText="원본은 그대로 보존되고, 내용을 복사한 새 초안이 만들어집니다."
            onConfirm={() => void svc.reviseJournal(journal.id).then(onChanged)}
          />
        )}
        {!locked && (
          <JournalPolish
            journalId={journal.id}
            projectUrl={projectUrl}
            beforeApply={() => a.flush()}
            onApplied={onChanged}
          />
        )}
        <details className="card">
          <summary>내부 메모 (공식 기록지에 포함되지 않음)</summary>
          <TextArea
            label="내부 메모"
            value={v.internalCounselorMemo}
            readOnly={locked}
            onChange={(x) => a.set('internalCounselorMemo', x || undefined)}
          />
        </details>
        {history}
      </aside>
    </div>
  );
}

/**
 * 외부 AI로 기록지 다듬기 (승인 전): 기록지 전체를 한 흐름으로 다듬어 받고, 칸마다 받을지 고른다.
 * 검사 칸은 앱이 채우는 칸이라 바꾸지 않는다. 그 회기의 위험 정보가 빠지면 알려준다.
 */
function JournalPolish({
  journalId,
  projectUrl,
  beforeApply,
  onApplied,
}: {
  journalId: string;
  projectUrl?: string | undefined;
  beforeApply: () => Promise<unknown>;
  onApplied: () => void;
}) {
  const app = useRequiredSession();
  const svc = useService();
  const ai = useMemo(() => new AiBridge(app.repo, app.ctx, svc), [app, svc]);
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState('');
  const { data: packet } = useLoad(
    async () => (open ? ai.journalPolishPacket(journalId, request) : null),
    [ai, journalId, open, request],
  );
  const [review, setReview] = useState<{ raw: string; preview: DocPreview } | null>(null);
  return (
    <details className="card" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>
        <strong>외부 AI로 다듬기</strong> <span className="muted small">기록지 전체를 한 흐름으로</span>
      </summary>
      <p className="muted small">
        회기 분석·메모·근거를 자료로 보내고, 칸별 작성 기준에 맞춰 기록지 전체를 다듬어 받아요. 받은 답은 칸마다 받을지
        골라 넣어요. 검사 칸은 바꾸지 않아요.
      </p>
      <label className="field">
        <span className="field-label">고칠 점 (선택)</span>
        <textarea
          rows={2}
          value={request}
          onChange={(e) => setRequest(e.target.value)}
          placeholder="예: 상담 내용을 더 간결하게, 다음 회기 계획에 과제를 구체적으로"
        />
      </label>
      {packet && <SendButton text={packet} projectUrl={projectUrl} label="외부로 보내기 (기록지)" />}
      <CopyButton text={POLISH_PROMPT} label="다듬기 요청 복사" />
      {review ? (
        <DocReview
          review={review}
          onCancel={() => setReview(null)}
          onApply={async (only) => {
            await beforeApply();
            await ai.applyJournalPolish(journalId, review.raw, only);
            setReview(null);
            onApplied();
          }}
        />
      ) : (
        <ReceiveBox
          title="다듬은 답 가져오기"
          hint="답 전체를 복사해 두면 돌아왔을 때 알아서 찾아요."
          onSubmit={async (raw) => {
            const pv = await ai.previewJournalPolish(journalId, raw);
            if (!pv.ok) throw new Error(pv.errors.join(', '));
            setReview({ raw, preview: pv });
          }}
        />
      )}
    </details>
  );
}
